import {
    put,
    take,
    takeEvery,
    select,
    race,
    fork,
    cancel,
    cancelled,
} from "redux-saga/effects";
import delay from "@redux-saga/delay-p";
import actionTypes from "data/actions/actionTypes";
import { getDocsAction, setDocsLayoutAction } from "data/actions";
import * as s from "data/reducers/selectors";
import { DocListFilterOperators, SearchValueType } from "data/types";
import {
    GetDefaults,
    ResetFilters,
    GetFilterValueAsString,
} from "components/DocListFilterModal/Utils";
import Constants, { getDefaultsConfig } from "data/constants";
import { produce } from "immer";
import { JL } from "jsnlog";

const log = JL("DocsLayoutSaga");

const resultSelector = (state) => state.docs;
const currentItemSelector = (state) => state.current;
const formatIdSelector = (state) =>
    s.breadcrumbFormatIdSelector(state, s.currentNameSelector(state));

// clears all filters & sorts (in case none is available, will reset everything)
function* resetFilters() {
    const result = yield select(resultSelector);
    const formatId = yield select(formatIdSelector);
    log.debug(() => ({
        msg: "resetting filters",
        result,
        formatId,
    }));
    if (result != null && result.cols != null && result.cols.length > 0) {
        yield put(
            setDocsLayoutAction({
                sorts: [],
                filters: ResetFilters(
                    result.itemUri,
                    formatId,
                    result.filters,
                    result.cols
                ),
            })
        );
    } else {
        log.debug("bruteforce - delete everything");
        yield put(setDocsLayoutAction());
    }
}

// create a filter based on a value referenced from the result.rows
function* setFilterByRef(action) {
    const status = yield select(s.docsStatusSelector);
    if (status !== Constants.OK) return;

    const { docContextClick, resetValues } = action.payload;
    const result = yield select(resultSelector);
    const formatId = yield select(formatIdSelector);
    //const filters = yield select(s.docsFiltersSelector);
    //const cols = yield select(s.docsColsSelector);
    /* istanbul ignore else */
    if (result != null && result.cols != null) {
        // get current filters
        let filters = result.filters;
        // none yet? create default list
        if (filters == null || filters.length === 0) {
            filters = GetDefaults(
                result.itemUri,
                formatId,
                result.cols
            ).filters;
        }
        if (resetValues) {
            // reset values ONLY (operators stay as they are)
            filters = ResetFilters(
                result.itemUri,
                formatId,
                filters,
                result.cols
            );
        }
        const { cols } = result;
        const colName = cols[docContextClick.columnIndex].name;

        // then set our own
        const newfilters = filters.map((filter, index) => {
            if (filter.name === colName) {
                return produce(filter, (draft) => {
                    draft.values = [
                        docContextClick.rowIndex === -1
                            ? ""
                            : GetFilterValueAsString(
                                  result.rows[docContextClick.rowIndex][
                                      docContextClick.columnIndex
                                  ]
                              ),
                    ];
                    if (
                        [
                            DocListFilterOperators.None,
                            DocListFilterOperators.Between,
                            DocListFilterOperators.NotBetween,
                            // DocListFilterOperators.Contains
                        ].includes(draft.operator)
                    )
                        draft.operator = DocListFilterOperators.Equals;
                    if (
                        [DocListFilterOperators.DoesNotContain].includes(
                            draft.operator
                        )
                    )
                        draft.operator = DocListFilterOperators.NotEquals;
                });
            }
            return filter;
        });
        yield put(setDocsLayoutAction({ filters: newfilters }));
        log.debug(() => ({
            msg: "set filter by reference",
            filters: newfilters,
        }));
    }
}

function* setFilterByName(action) {
    const result = yield select(resultSelector);
    const formatId = yield select(formatIdSelector);
    // don't do anything on initial FlatAU load (pageNum=-1)
    /* istanbul ignore else */
    if (result == null || result.cols == null || result.pageNum === -1) return;

    const { name, valueType, operator, value } = action.payload;
    // get current filters
    let filters = result.filters;
    // none yet? create default list
    if (filters == null || filters.length === 0) {
        filters = GetDefaults(result.itemUri, formatId, result.cols).filters;
    }

    // new value to assign; reset or set value
    let newValue =
        value === "__ALL__" ||
        (valueType === SearchValueType.RelativeDate && value === "NotSet")
            ? ""
            : value;
    // Transform to Array (if not already an array)
    if (!Array.isArray(newValue)) {
        newValue = [newValue];
    }
    // find requested filter
    const idx = filters.findIndex((f) => f.name === name);
    let newFilters = filters;
    if (idx === -1) {
        // add new filter
        newFilters = produce(filters, (draft) => {
            draft.push({
                name,
                operator,
                values: newValue,
            });
        });
    } else {
        newFilters = produce(filters, (draft) => {
            draft[idx] = {
                name,
                operator: operator || filters[idx].operator,
                values: newValue,
            };
        });
    }
    // publish the changed filters
    yield put(setDocsLayoutAction({ filters: newFilters }));
}

function* waitUntilCurrentItemUriMatches(itemUri) {
    while (true) {
        const currentItem = yield select(currentItemSelector);
        if (currentItem.itemUri === itemUri) {
            return;
        }
        yield delay(100);
    }
}

function* getDocsLayout(action) {
    try {
        // wait until currentItemUri matches or timeout
        const waitTask = yield fork(
            waitUntilCurrentItemUriMatches,
            action.payload.itemUri
        );
        const { timeout } = yield race({
            wait: waitTask,
            timeout: delay(5000),
        });

        /* istanbul ignore next */
        if (timeout != null) {
            log.error(
                "Timeout occurred while waiting for currentItemUri to match"
            );
            yield cancel(waitTask);
        }
    } catch (error) {
        /* istanbul ignore next */
        log.error(() => ({
            msg: "Error occurred while waiting for currentItemUri to match",
            error,
        }));
    }

    // restore layout from currentItem
    const currentItem = yield select(currentItemSelector);
    if (currentItem?.layout != null) {
        yield put(setDocsLayoutAction(currentItem.layout));
        log.debug(() => ({
            msg: "restored layout",
            layout: currentItem.layout,
        }));
        return;
    }

    // or maybe defaults config?
    const defaults = getDefaultsConfig(
        action.payload.itemUri,
        action.payload.formatId
    );
    /* istanbul ignore else */
    if (defaults !== null) {
        const { filters, sorts } = defaults;
        log.debug("found defaults...");
        /* istanbul ignore else */
        if (/* istanbul ignore next */ filters != null || sorts != null) {
            yield put(
                setDocsLayoutAction({
                    filters: filters ?? /* istanbul ignore next */ [],
                    sorts: sorts ?? /* istanbul ignore next */ [],
                })
            );
            log.debug(() => ({
                msg: "set defaults",
                layout: { filters, sorts },
            }));
            return;
        }
    }

    // create default
    const result = yield select(resultSelector);
    if (result?.cols != null) {
        const layout = GetDefaults(
            action.payload.itemUri,
            action.payload.formatId,
            result.cols
        );
        yield put(setDocsLayoutAction(layout));
        log.debug(() => ({
            msg: "set defaults",
            layout,
            cols: result.cols,
        }));
        return;
    } else if (action.payload.allowPreFetch === true) {
        // only if param "allowPreFetch" is set! (related to showDocs=true and immediately showing filters)
        const currentItem = yield select(currentItemSelector);
        yield put(
            getDocsAction({ pageNum: -1, itemUri: action.payload.itemUri })
        );
        const docsresult = yield take(actionTypes.DOCS_FETCH_RESULT);
        const layout = GetDefaults(
            action.payload.itemUri,
            action.payload.formatId,
            docsresult.payload.result.cols
        );
        yield put(setDocsLayoutAction(layout));
        log.debug(() => ({
            msg: "set defaults (prefetched)",
            layout,
            cols: docsresult.payload.result.cols,
        }));
        return;
    }
    log.debug("setting no layout");
    yield put(setDocsLayoutAction());
}

function* setFilterText(action) {
    yield setFilterByName(action);
}

function* docsLayoutSaga() {
    yield takeEvery(actionTypes.DOCS_FILTERS_RESET, resetFilters);
    yield takeEvery(actionTypes.DOCS_FILTERS_SETBYREF, setFilterByRef);
    yield takeEvery(actionTypes.DOCS_FILTERS_SETBYNAME, setFilterByName);
    yield takeEvery(actionTypes.DOCS_FILTERTEXT_SET, setFilterText);

    yield takeEvery(actionTypes.DOCS_LAYOUT_REQUEST, getDocsLayout);
}

export default docsLayoutSaga;
